/**
 * #64/#74 — Bornes d'une absence à la demi-journée.
 *
 * Deux champs indépendants (`startHalf`, `endHalf`) et non un seul : « je pars
 * le 10 à midi et je rentre le 12 à midi » doit être exprimable. L'export
 * Gescom ne produit jamais ce cas, mais la saisie directe dans Synolog, si.
 *
 * Seule combinaison refusée, sur une absence d'UN SEUL jour : commencer
 * l'après-midi et finir le matin, qui décrirait une durée négative. L'option
 * est alors désactivée plutôt que signalée après coup.
 */

const OPTION_STYLE = (active, disabled) => ({
  padding: '5px 12px',
  borderRadius: 999,
  border: `1px solid ${active ? 'var(--primary)' : 'var(--glass-border)'}`,
  background: active ? 'var(--primary)' : 'transparent',
  color: active ? '#fff' : 'var(--text-muted)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  fontSize: 12,
  lineHeight: 1.4,
  transition: 'background 120ms, border-color 120ms, color 120ms',
});

function Pills({ label, options, value, onChange, disabled }) {
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <span style={{ display: 'block', marginBottom: 6, fontSize: 12, color: 'var(--text-muted)' }}>
        {label}
      </span>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            disabled={disabled || o.disabled}
            title={o.title}
            style={OPTION_STYLE(value === o.value, disabled || o.disabled)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function HalfDayPicker({
  startHalf = 'AM',
  endHalf = 'PM',
  onChange,
  disabled = false,
  sameDay = false,
}) {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
      <Pills
        label="Départ"
        value={startHalf}
        disabled={disabled}
        onChange={(v) => onChange({ startHalf: v, endHalf: sameDay && v === 'PM' ? 'PM' : endHalf })}
        options={[
          { value: 'AM', label: 'Matin', title: "L'absence démarre le matin" },
          { value: 'PM', label: 'Après-midi', title: "L'absence démarre l'après-midi" },
        ]}
      />
      <Pills
        label="Retour"
        value={endHalf}
        disabled={disabled}
        onChange={(v) => onChange({ startHalf, endHalf: v })}
        options={[
          {
            value: 'AM',
            label: 'Matin',
            title: sameDay && startHalf === 'PM'
              ? "Impossible : l'absence démarre déjà l'après-midi ce jour-là"
              : "L'absence se termine à midi (matinée incluse)",
            // Sur un seul jour, PM → AM serait de durée négative.
            disabled: sameDay && startHalf === 'PM',
          },
          { value: 'PM', label: 'Soir', title: "L'absence se termine en fin de journée" },
        ]}
      />
    </div>
  );
}

/** `true` si l'absence n'est pas bornée en journées pleines. */
export function hasHalfDays(t) {
  return (t?.startHalf ?? 'AM') !== 'AM' || (t?.endHalf ?? 'PM') !== 'PM';
}

/** Libellé court des bornes, pour les listes et le planning. */
export function halfDayLabel(t, { short = false } = {}) {
  const s = t?.startHalf ?? 'AM';
  const e = t?.endHalf ?? 'PM';
  if (s === 'AM' && e === 'PM') return null;
  const sameDay = t?.startDate && t?.endDate
    ? String(t.startDate).slice(0, 10) === String(t.endDate).slice(0, 10)
    : false;
  if (sameDay) {
    if (s === 'AM' && e === 'AM') return short ? '½ matin' : 'Matinée';
    if (s === 'PM' && e === 'PM') return short ? '½ a.m.' : 'Après-midi';
  }
  if (s === 'PM' && e === 'AM') return short ? 'a.m. → matin' : "Départ l'après-midi, retour le matin";
  if (s === 'PM') return short ? 'dep. a.m.' : "Départ l'après-midi";
  return short ? 'ret. matin' : 'Retour le matin';
}

/**
 * Part de la journée `isoDay` couverte par l'absence : `1` ou `0.5`.
 *
 * Les demi-journées ne s'appliquent qu'aux jours de BORD — un congé de trois
 * jours démarré l'après-midi a ses jours intérieurs pleins.
 *
 * Cas particulier du congé d'un seul jour, où les deux bornes tombent le même
 * jour : `AM→AM` et `PM→PM` valent une demi-journée, `AM→PM` la journée
 * entière. Traiter les deux bornes indépendamment y appliquerait deux fois la
 * réduction et donnerait zéro. Même règle que `capacity.ts` côté serveur.
 */
export function dayCoverageFactor(t, isoDay) {
  if (!t || t.fullDay === false) return 1;
  const start = String(t.startDate ?? '').slice(0, 10);
  const end = String(t.endDate ?? '').slice(0, 10);
  const day = String(isoDay ?? '').slice(0, 10);
  const startHalf = t.startHalf ?? 'AM';
  const endHalf = t.endHalf ?? 'PM';
  const isFirst = day === start;
  const isLast = day === end;
  if (isFirst && isLast) return startHalf === endHalf ? 0.5 : 1;
  if (isFirst) return startHalf === 'PM' ? 0.5 : 1;
  if (isLast) return endHalf === 'AM' ? 0.5 : 1;
  return 1;
}

/**
 * Libellé de durée d'un congé, demi-journées comprises.
 * Utilisé partout où l'on affichait « Jour entier » / « Nh ».
 */
export function timeOffDurationLabel(t) {
  if (!t.fullDay) return `${Number(t.hoursPerDay)}h`;
  return halfDayLabel(t, { short: true }) ?? 'Jour entier';
}

/**
 * #75 — URL de la demande d'absence dans Gescom.
 *
 * Base sortie en configuration : staging et production ne pointent pas
 * forcément la même instance. `null` si le congé n'a pas d'origine Gescom.
 */
const GESCOM_BASE_URL =
  import.meta.env.VITE_GESCOM_BASE_URL || 'https://gescom-synotis.smile.eu';

export function gescomRequestUrl(gescomAbsenceId) {
  if (!gescomAbsenceId) return null;
  return `${GESCOM_BASE_URL}/rh/CONGE/collab_holiday_viewRequest.aspx?RequestID=${encodeURIComponent(gescomAbsenceId)}`;
}
